// router/index.js
import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";

// Import views
import HomeView from "@/views/HomeView.vue";
import FeedView from "@/views/FeedView.vue";
import CourseView from "@/views/CourseView.vue";
import CourseStudioView from "@/views/CourseStudioView.vue";
import SearchResultsView from "@/views/SearchResultsView.vue";
import ProfileView from "@/views/ProfileView.vue";
import CreatorProfileView from "@/views/CreatorProfileView.vue";
import CheckoutView from "@/views/CheckoutView.vue";
import AboutView from "@/views/AboutView.vue";

const routes = [
  {
    path: "/",
    name: "home",
    component: () => import("@/views/HomeView.vue"),
    meta: { requiresAuth: false },
  },
  {
    path: "/feed",
    name: "feed",
    component: () => import("@/views/FeedView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/explore",
    name: "explore",
    component: HomeView,
    meta: {
      requiresAuth: false,
      title: "Explore Courses - MyHarf",
    },
  },
  {
    path: "/course/:id",
    name: "course",
    component: CourseView,
    props: true,
    meta: {
      requiresAuth: false,
      title: "Course - MyHarf",
    },
  },
  {
    path: "/studio",
    name: "studio",
    component: CourseStudioView,
    meta: {
      requiresAuth: true,
      title: "Course Studio - MyHarf",
    },
  },
  {
    path: "/studio/:courseId",
    name: "studioEdit",
    component: CourseStudioView,
    props: true,
    meta: {
      requiresAuth: true,
      title: "Edit Course - MyHarf",
    },
  },
  {
    path: "/search",
    name: "search",
    component: SearchResultsView,
    meta: {
      requiresAuth: false,
      title: "Search Results - MyHarf",
    },
  },
  {
    path: "/course/preview/:id",
    name: "coursePreview",
    component: CourseView,
    props: true,
    meta: {
      requiresAuth: true,
      title: "Course Preview - MyHarf",
    },
  },
  {
    path: "/profile",
    name: "profile",
    component: ProfileView,
    meta: {
      requiresAuth: true,
      title: "My Profile - MyHarf",
    },
  },
  {
    path: "/profile/:id",
    name: "userProfile",
    component: CreatorProfileView,
    props: true,
    meta: {
      requiresAuth: false,
      title: "User Profile - MyHarf",
    },
  },
  {
    path: "/creator/:id",
    name: "creator",
    component: CreatorProfileView,
    props: true,
    meta: {
      requiresAuth: false,
      title: "Creator - MyHarf",
    },
  },
  {
    path: "/checkout/:courseId",
    name: "checkout",
    component: CheckoutView,
    props: true,
    meta: {
      requiresAuth: true,
      title: "Checkout - MyHarf",
    },
  },
  {
    path: "/about",
    name: "about",
    component: AboutView,
    meta: {
      requiresAuth: false,
      title: "About - MyHarf",
    },
  },
  // Catch-all route for 404
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("@/views/NotFoundView.vue"),
    meta: {
      requiresAuth: false,
      title: "Page Not Found - MyHarf",
    },
  },
];

// Create router with history mode (not hash)
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

// Navigation Guard
router.beforeEach(async (to, from, next) => {
  document.title = to.meta.title || "MyHarf";

  const authStore = useAuthStore();

  if (authStore.loading) {
    await new Promise((resolve) => {
      const unwatch = authStore.$subscribe(() => {
        if (!authStore.loading) {
          unwatch();
          resolve();
        }
      });
    });
  }

  if (to.meta.requiresAuth) {
    if (!authStore.isAuthenticated) {
      return next({
        path: "/",
        query: { redirect: to.fullPath },
      });
    }

    if (
      to.meta.requiresCreator &&
      authStore.user?.role !== "creator" &&
      authStore.user?.role !== "admin"
    ) {
      return next({ path: "/" });
    }
  }

  next();
});

router.afterEach((to, from) => {
  console.log(`📄 Navigated to: ${to.path} (${to.meta.title || "Untitled"})`);
});

export default router;
